import * as actionTypes from "./actionTypes";
import apiClient from "../../services/apiClient";
import { getMerchantId } from "../../utils/tokenManager";
import { showErrorToast, showSuccessToast } from "../../utils/errorHandler";

// ==============================
// LIST: GET AGGREGATED ORDERS
// ==============================

export const getOrdersStart = () => ({
  type: actionTypes.GET_ORDERS_START,
});

export const getOrdersSuccess = (data) => ({
  type: actionTypes.GET_ORDERS_SUCCESS,
  data,
});

export const getOrdersFail = () => ({
  type: actionTypes.GET_ORDERS_FAIL,
});

/**
 * Fetch all aggregated orders for the logged-in merchant.
 * Replaces the old individual-orders getOrders thunk.
 * Endpoint: GET /api/aggregated-orders/by-merchant/:merchantId
 */
export const getAggregatedOrders = () => {
  return async (dispatch) => {
    dispatch(getOrdersStart());

    try {
      const merchantId = getMerchantId();

      if (!merchantId) {
        showErrorToast({ message: "Merchant ID not found. Please log in again." });
        dispatch(getOrdersFail());
        return;
      }

      const response = await apiClient.get(`/aggregated-orders/by-merchant/${merchantId}`);
      const data = response.data.data; // flat array of AggregatedOrderWithDetails

      dispatch(getOrdersSuccess(data));
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to fetch aggregated orders";
      showErrorToast({ message: errorMessage });
      dispatch(getOrdersFail());
    }
  };
};

// Keep legacy export alias so Dashboard/Layout code that dispatches getOrders() still compiles
export const getOrders = getAggregatedOrders;

// ==============================
// DETAIL: GET ONE AGGREGATED ORDER
// ==============================

export const getOneAggOrderStart = () => ({
  type: actionTypes.GET_ONE_AGG_ORDER_START,
});

export const getOneAggOrderSuccess = (data) => ({
  type: actionTypes.GET_ONE_AGG_ORDER_SUCCESS,
  data,
});

export const getOneAggOrderFail = () => ({
  type: actionTypes.GET_ONE_AGG_ORDER_FAIL,
});

/**
 * Fetch a single aggregated order by ID.
 * Endpoint: GET /api/aggregated-orders/:id
 */
export const getOneAggregatedOrder = (id) => {
  return async (dispatch) => {
    dispatch(getOneAggOrderStart());

    try {
      const response = await apiClient.get(`/aggregated-orders/${id}`);
      const data = response.data.data;
      dispatch(getOneAggOrderSuccess(data));
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to fetch aggregated order details";
      showErrorToast({ message: errorMessage });
      dispatch(getOneAggOrderFail());
    }
  };
};

// ==============================
// ACTION: CONFIRM AGGREGATED ORDER
// ==============================

export const confirmAggOrderStart = () => ({
  type: actionTypes.CONFIRM_AGG_ORDER_START,
});

export const confirmAggOrderSuccess = () => ({
  type: actionTypes.CONFIRM_AGG_ORDER_SUCCESS,
});

export const confirmAggOrderFail = () => ({
  type: actionTypes.CONFIRM_AGG_ORDER_FAIL,
});

/**
 * Confirm a PENDING_CONFIRMATION aggregated order → moves to CONFIRMED.
 * Endpoint: PUT /api/aggregated-orders/:id/confirm
 * @param {number} id - aggregated order ID
 * @param {function} cb - callback to run after success (e.g. refresh details)
 */
export const confirmAggregatedOrder = (id, cb) => {
  return async (dispatch) => {
    dispatch(confirmAggOrderStart());

    try {
      await apiClient.put(`/aggregated-orders/${id}/confirm`, {});
      showSuccessToast({ message: "Order confirmed successfully" });
      dispatch(confirmAggOrderSuccess());
      if (cb) cb();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to confirm aggregated order";
      showErrorToast({ message: errorMessage });
      dispatch(confirmAggOrderFail());
    }
  };
};

// ==============================
// ACTION: UPDATE AGGREGATED ORDER ITEM
// ==============================

export const updateAggItemStart = () => ({
  type: actionTypes.UPDATE_AGG_ORDER_ITEM_START,
});

export const updateAggItemSuccess = () => ({
  type: actionTypes.UPDATE_AGG_ORDER_ITEM_SUCCESS,
});

export const updateAggItemFail = () => ({
  type: actionTypes.UPDATE_AGG_ORDER_ITEM_FAIL,
});

/**
 * Update a single aggregated order item (confirmedQuantity OR rejectionReason).
 * Endpoint: PUT /api/aggregated-orders/:aggOrderId/items/:itemId
 * @param {number} aggOrderId - aggregated order ID
 * @param {number} itemId - item ID
 * @param {object} payload - { confirmedQuantity: number } OR { rejectionReason: string }
 * @param {function} cb - callback on success (e.g. refresh order)
 */
export const updateAggregatedOrderItem = (aggOrderId, itemId, payload, cb) => {
  return async (dispatch) => {
    dispatch(updateAggItemStart());

    try {
      await apiClient.put(`/aggregated-orders/${aggOrderId}/items/${itemId}`, payload);
      dispatch(updateAggItemSuccess());
      if (cb) cb();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update item";
      showErrorToast({ message: errorMessage });
      dispatch(updateAggItemFail());
    }
  };
};

// ==============================
// LEGACY: INDIVIDUAL ORDER ACTIONS
// (kept for backward compatibility with existing OrderDetails.js)
// ==============================

export const getOneOrderStart = () => ({
  type: actionTypes.GET_ONE_ORDER_START,
});

export const getOneOrderSuccess = (data) => ({
  type: actionTypes.GET_ONE_ORDER_SUCCESS,
  data,
});

export const getOneOrderFail = () => ({
  type: actionTypes.GET_ONE_ORDER_FAIL,
});

export const getOneOrders = (id) => {
  return async (dispatch) => {
    dispatch(getOneOrderStart());

    try {
      const response = await apiClient.get(`/orders/${id}`);
      const orderData = response.data.data;
      dispatch(getOneOrderSuccess(orderData));
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to fetch order details";
      showErrorToast({ message: errorMessage });
      dispatch(getOneOrderFail());
    }
  };
};

export const confirmOrderStart = () => ({
  type: actionTypes.CONFIRM_ORDER_START,
});

export const confirmOrderSuccess = (data) => ({
  type: actionTypes.CONFIRM_ORDER_SUCCESS,
  data,
});

export const confirmOrderFail = () => ({
  type: actionTypes.CONFIRM_ORDER_FAIL,
});

export const confirmOrder = (orderID, action, cb) => {
  return async (dispatch) => {
    dispatch(confirmOrderStart());

    try {
      const statusMap = {
        confirm: "CONFIRMED",
        prepare: "PREPARING",
        ready: "READY",
        delivered: "DELIVERED",
      };

      const response = await apiClient.put(`/orders/${orderID}/${action}`, {
        status: statusMap[action],
      });

      const updatedOrder = response.data.data;
      showSuccessToast({ message: "Order status updated successfully" });
      dispatch(confirmOrderSuccess(updatedOrder));

      if (cb) cb();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update order status";
      showErrorToast({ message: errorMessage });
      dispatch(confirmOrderFail());
    }
  };
};

export const rejectProductStart = () => ({
  type: actionTypes.REJECT_PRODUCT_START,
});

export const rejectProductSuccess = () => ({
  type: actionTypes.REJECT_PRODUCT_SUCCESS,
});

export const rejectProductFail = () => ({
  type: actionTypes.REJECT_PRODUCT_FAIL,
});

export const rejectProduct = (order, cb) => {
  return async (dispatch) => {
    dispatch(rejectProductStart());

    try {
      await apiClient.put(`/orders/${order.id}`, order);
      showSuccessToast({ message: "Product rejection saved" });
      dispatch(rejectProductSuccess());
      dispatch(getOneOrders(order.id));
      if (cb) cb();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to reject product";
      showErrorToast({ message: errorMessage });
      dispatch(rejectProductFail());
    }
  };
};

// ==============================
// PAGINATION / FILTER HELPERS (kept for inventory page compatibility)
// ==============================

export const updatePerPageSelectStart = (name, value) => ({
  type: actionTypes.UPDATE_SELECT_PER_PAGE,
  value,
  name,
});

export const updatePageSelectStart = (value) => ({
  type: actionTypes.UPDATE_SELECT_PAGE,
  value,
});

export const updatePerPageSelect = (name, value) => (dispatch) => {
  dispatch(updatePerPageSelectStart(name, value));
};

export const updatePageSelect = (value) => (dispatch) => {
  dispatch(updatePerPageSelectStart(value));
};
